import { Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs';
import { AppModeService } from 'src/app/services/app-mode.service';
import { AuthService } from 'src/app/services/auth/auth.service';

@Component({
  selector: 'app-verify-otp',
  templateUrl: './verify-otp.component.html',
  styleUrls: ['./verify-otp.component.css'],
})
export class VerifyOtpComponent implements OnInit, OnDestroy {
  isMobileApp = false;
  email = '';
  otpValues: string[] = ['', '', '', '', ''];
  isLoading = false;
  otpError = '';
  requestError = '';
  successMessage = '';
  isOtpShaking = false;
  resendCountdown = 30;
  canResend = false;
  isResending = false;

  @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef>;

  private countdownInterval: any;

  constructor(
    private readonly appModeService: AppModeService,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.isMobileApp = this.appModeService.isApp();
    this.email = this.router.getCurrentNavigation()?.extras.state?.['email'] || '';
    if (!this.email) {
      this.email = sessionStorage.getItem('pwResetEmail') ?? '';
    }

    if (!this.email) {
      this.navigateBack();
      return;
    }

    this.startCountdown();
  }

  ngOnDestroy(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }

  get isOtpComplete(): boolean {
    return this.otpValues.every((value) => value !== '');
  }

  getOtpCode(): string {
    return this.otpValues.join('');
  }

  onOtpInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const rawValue = input.value || '';
    const lastChar = rawValue.slice(-1);
    const isDigit = /^\d$/.test(lastChar);

    this.otpValues[index] = isDigit ? lastChar : '';
    input.value = this.otpValues[index];

    if (isDigit && index < 4) {
      this.otpInputs.toArray()[index + 1]?.nativeElement.focus();
    }

    if (this.isOtpComplete) {
      this.validateAndSubmit();
    }
  }

  onOtpKeydown(event: KeyboardEvent, index: number): void {
    if (event.key === 'Backspace') {
      if (this.otpValues[index] === '' && index > 0) {
        this.otpValues[index - 1] = '';
        this.otpInputs.toArray()[index - 1]?.nativeElement.focus();
      }
      return;
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      this.otpInputs.toArray()[index - 1]?.nativeElement.focus();
      return;
    }

    if (event.key === 'ArrowRight' && index < 4) {
      event.preventDefault();
      this.otpInputs.toArray()[index + 1]?.nativeElement.focus();
    }
  }

  onOtpPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text') ?? '';
    const digits = pasted.replace(/\D/g, '').slice(0, 5).split('');

    this.otpValues = ['', '', '', '', ''];
    digits.forEach((digit, index) => {
      this.otpValues[index] = digit;
    });

    const focusIndex = digits.length > 0 ? Math.min(digits.length - 1, 4) : 0;
    this.otpInputs.toArray()[focusIndex]?.nativeElement.focus();
  }

  validateAndSubmit(): void {
    this.otpError = '';
    this.requestError = '';
    this.successMessage = '';

    if (!this.isOtpComplete) {
      this.otpError = 'VERIFY_OTP.ERROR_INCOMPLETE';
      this.triggerShake();
      return;
    }

    this.isLoading = true;
    this.authService
      .verifyOtp(this.email, this.getOtpCode())
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: () => {
          this.successMessage = 'VERIFY_OTP.SUCCESS_VERIFIED';
          const lang = this.route.snapshot.paramMap.get('lang') || 'en';
          setTimeout(() => {
            this.router.navigate([lang, 'create-password'], { state: { email: this.email } });
          }, 1000);
        },
        error: () => {
          this.requestError = 'VERIFY_OTP.ERROR_INVALID_CODE';
          this.triggerShake();
        },
      });
  }

  resendCode(): void {
    if (!this.canResend || this.isResending) {
      return;
    }

    this.isResending = true;
    this.authService
      .forgotPassword(this.email)
      .pipe(finalize(() => (this.isResending = false)))
      .subscribe({
        next: () => {
          this.otpValues = ['', '', '', '', ''];
          this.otpError = '';
          this.requestError = '';
          this.successMessage = '';
          this.resendCountdown = 30;
          this.canResend = false;
          this.startCountdown();
        },
        error: () => {
          this.requestError = 'VERIFY_OTP.ERROR_RESEND_FAILED';
        },
      });
  }

  navigateBack(): void {
    const lang = this.route.snapshot.paramMap.get('lang') || 'en';
    this.router.navigate([lang, 'forgot-password']);
  }

  private triggerShake(): void {
    this.isOtpShaking = true;
    setTimeout(() => {
      this.isOtpShaking = false;
    }, 600);
  }

  private startCountdown(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }

    this.countdownInterval = setInterval(() => {
      if (this.resendCountdown > 0) {
        this.resendCountdown -= 1;
        return;
      }

      this.canResend = true;
      clearInterval(this.countdownInterval);
    }, 1000);
  }
}
